// Floor Easy product form and free-sample cart handler
// Updated: 2026-09-01

if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        if (this.dataset.sellingUnit === "unit" && !this.form.reportValidity()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Custom code for packs and quantity.
        if (this.dataset.sellingUnit === "unit") {
          const quantity = Number(formData.get("quantity"));
          formData.set("quantity", String(quantity));
        } else {
          const productScope = this.closest("product-info") || this.closest(".shopify-section");
          const calculatedPacks = productScope?.querySelector("#calculated_packs");
          if (calculatedPacks) formData.set("quantity", calculatedPacks.value);
        }

        config.body = formData;
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

// A single cart manager keeps every sample button on the page synchronized.
var FloorEasySampleCart = window.FloorEasySampleCart || {
  maxSamples: 4,
  instances: new Set(),
  cart: null,
  loadPromise: null,
  busy: false,
  activeInstance: null,
  popupTimer: null,
  popupDuration: 7000,

  route(path) {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}${path}`;
  },

  register(instance) {
    // Load the shared button-state styles immediately, including on return visits.
    this.ensurePopup();
    this.instances.add(instance);
    if (this.cart) {
      instance.renderState(this.getState());
    } else {
      this.loadCart().catch((error) => console.error(error));
    }
  },

  unregister(instance) {
    this.instances.delete(instance);
  },

  isSampleItem(item) {
    const variantTitle = String(item.variant_title || "").toLowerCase();
    const optionHasSample = Array.isArray(item.options_with_values)
      ? item.options_with_values.some((option) =>
          String(option.value || "").toLowerCase().includes("sample")
        )
      : false;

    return variantTitle.includes("sample") || optionHasSample;
  },

  getState() {
    const items = Array.isArray(this.cart?.items) ? this.cart.items : [];
    const sampleItems = items.filter((item) => this.isSampleItem(item));
    const count = sampleItems.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    );

    return {
      count,
      limitReached: count >= this.maxSamples,
      variantIds: new Set(
        sampleItems.map((item) => String(item.variant_id || item.id))
      ),
      busy: this.busy,
      activeInstance: this.activeInstance,
    };
  },

  syncButtons() {
    const state = this.getState();
    this.instances.forEach((instance) => instance.renderState(state));
  },

  async loadCart(force = false) {
    if (force) this.loadPromise = null;
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = fetch(this.route("cart.js"), {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to read the basket.");
        this.cart = await response.json();
        this.syncButtons();
        return this.cart;
      })
      .catch((error) => {
        this.loadPromise = null;
        throw error;
      });

    return this.loadPromise;
  },

  async add(instance) {
    if (this.busy) return;

    try {
      await this.loadCart();
      let state = this.getState();

      if (state.variantIds.has(String(instance.variantId))) {
        instance.renderState(state);
        return;
      }

      if (state.limitReached) {
        this.syncButtons();
        this.showLimitMessage(state.count);
        return;
      }

      this.busy = true;
      this.activeInstance = instance;
      this.syncButtons();

      const response = await fetch(this.route("cart/add.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: [{ id: Number(instance.variantId), quantity: 1 }],
        }),
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(
          result.description || result.message || "Unable to add this sample."
        );
      }

      const addedItem = Array.isArray(result.items)
        ? result.items[0]
        : result;

      await this.loadCart(true);
      state = this.getState();
      await this.updateCartIcon();
      this.showSuccessPopup(addedItem, state.count);

      if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "sample-button",
          productVariantId: instance.variantId,
          cartData: this.cart,
        });
      }
    } catch (error) {
      console.error("Free sample error:", error);
      this.showErrorPopup(
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      this.busy = false;
      this.activeInstance = null;
      this.syncButtons();
    }
  },

  async updateCartIcon() {
    const currentBubble = document.querySelector("#cart-icon-bubble");
    let dawnBubbleUpdated = false;

    try {
      const response = await fetch(
        `${this.route("cart")}?section_id=cart-icon-bubble`,
        {
          credentials: "same-origin",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        }
      );

      if (!response.ok) throw new Error("Unable to refresh cart icon.");
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const freshBubble = parsed.querySelector("#cart-icon-bubble");

      if (currentBubble && freshBubble) {
        currentBubble.replaceWith(freshBubble);
        dawnBubbleUpdated = true;
      }
    } catch (error) {
      console.warn(error);
    }

    // Safe fallback if the section cannot be refreshed.
    const count = Number(this.cart?.item_count || 0);

    if (!dawnBubbleUpdated) {
      const bubble = document.querySelector("#cart-icon-bubble");
      if (bubble) {
        let countBubble = bubble.querySelector(".cart-count-bubble");
        if (!count) {
          countBubble?.remove();
        } else {
          if (!countBubble) {
            countBubble = document.createElement("div");
            countBubble.className = "cart-count-bubble";
            countBubble.innerHTML =
              '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
            bubble.appendChild(countBubble);
          }

          const visibleCount = countBubble.querySelector('[aria-hidden="true"]');
          const accessibleCount = countBubble.querySelector(".visually-hidden");
          if (visibleCount) visibleCount.textContent = String(count);
          if (accessibleCount)
            accessibleCount.textContent = `${count} ${
              count === 1 ? "item" : "items"
            }`;
        }
      }
    }

    // The live Floor Easy header uses its own cart-count markup.
    document
      .querySelectorAll(
        'a.fe-custom-navigation__icon-button[href="/cart"], a[aria-label="Shopping cart"][href="/cart"]'
      )
      .forEach((cartLink) => {
        let visibleCount = cartLink.querySelector(
          ".fe-custom-navigation__cart-count"
        );
        let accessibleCount = cartLink.querySelector(
          ".fe-custom-navigation__visually-hidden"
        );

        if (!count) {
          visibleCount?.remove();
          accessibleCount?.remove();
          return;
        }

        if (!visibleCount) {
          visibleCount = document.createElement("span");
          visibleCount.className = "fe-custom-navigation__cart-count";
          visibleCount.setAttribute("aria-hidden", "true");
          cartLink.appendChild(visibleCount);
        }
        if (!accessibleCount) {
          accessibleCount = document.createElement("span");
          accessibleCount.className =
            "fe-custom-navigation__visually-hidden";
          cartLink.appendChild(accessibleCount);
        }

        visibleCount.textContent = String(count);
        accessibleCount.textContent = `${count} ${
          count === 1 ? "item" : "items"
        }`;
      });
  },

  ensurePopup() {
    let popup = document.getElementById("fe-sample-popup");
    if (popup) return popup;

    const style = document.createElement("style");
    style.id = "fe-sample-popup-styles";
    style.textContent = `
      .quickadd-sample.sample-added {
        background: #2fad63 !important;
        border-color: #2fad63 !important;
        color: #fff !important;
        opacity: 1 !important;
      }
      .quickadd-sample.sample-limit-reached {
        background: #e8ece9 !important;
        border-color: #d5ddd7 !important;
        color: #31503b !important;
        opacity: 1 !important;
      }
      .quickadd-sample[disabled] { cursor: not-allowed !important; }
      .quickadd-sample.loading .sample-button__label { opacity: 0; }

      #fe-sample-popup {
        position: fixed;
        z-index: 2147483000;
        top: clamp(110px, 15vh, 170px);
        right: 24px;
        width: min(410px, calc(100vw - 32px));
        max-height: calc(100dvh - clamp(110px, 15vh, 170px) - 24px);
        overflow-y: auto;
        border: 1px solid #dde2d8;
        border-radius: 6px;
        background: #fff;
        color: #242923;
        font-family: var(--font-body-family, sans-serif);
        font-style: var(--font-body-style, normal);
        letter-spacing: 0;
        box-shadow: 0 12px 36px rgba(29, 39, 26, 0.14);
        opacity: 0;
        transform: translateY(-8px);
        pointer-events: none;
        transition: opacity .2s ease, transform .2s ease;
      }
      #fe-sample-popup.fe-sample-popup--visible {
        opacity: 1;
        transform: translateY(0);
        pointer-events: auto;
      }
      .fe-sample-popup__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 16px 14px 20px;
        border-bottom: 1px solid #e2e7dc;
        background: #f3f5ef;
        color: #344c33;
      }
      .fe-sample-popup__header.is-error {
        border-color: #ecd8d5;
        background: #faf0ee;
        color: #8a3330;
      }
      .fe-sample-popup__heading {
        margin: 0;
        color: inherit;
        font-family: inherit;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.45;
        letter-spacing: 0;
      }
      .fe-sample-popup__close {
        display: grid;
        place-items: center;
        flex: 0 0 44px;
        width: 44px;
        height: 44px;
        padding: 0;
        border: 0;
        border-radius: 3px;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }
      .fe-sample-popup__close svg { display: block; width: 18px; height: 18px; }
      .fe-sample-popup__close:hover { background: rgba(69, 100, 68, .08); }
      .fe-sample-popup__body { padding: 20px; }
      .fe-sample-popup__product {
        display: grid;
        grid-template-columns: 76px minmax(0, 1fr);
        align-items: center;
        gap: 16px;
        min-height: 76px;
      }
      .fe-sample-popup__image {
        width: 76px;
        height: 76px;
        border: 1px solid #e7e9e3;
        border-radius: 3px;
        object-fit: cover;
        background: #f3f5ef;
      }
      .fe-sample-popup__image[hidden] { display: none; }
      .fe-sample-popup__product:has(.fe-sample-popup__image[hidden]) {
        grid-template-columns: 1fr;
      }
      .fe-sample-popup__title {
        margin: 0 0 6px;
        color: #242923;
        font-family: inherit;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.5;
        overflow-wrap: anywhere;
      }
      .fe-sample-popup__message {
        margin: 0;
        color: #60675d;
        font-size: 13px;
        line-height: 1.5;
      }
      .fe-sample-popup__basket {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 48px;
        margin-top: 20px;
        padding: 12px 16px;
        border: 1px solid #456444;
        border-radius: 2px;
        background: #456444;
        color: #fff;
        font-family: inherit;
        font-size: 15px;
        font-weight: 500;
        line-height: 1.5;
        text-decoration: none;
      }
      .fe-sample-popup__basket:hover {
        border-color: #354f34;
        background: #354f34;
        color: #fff;
      }
      .fe-sample-popup__continue {
        display: block;
        width: 100%;
        min-height: 44px;
        margin-top: 8px;
        padding: 10px;
        border: 0;
        border-radius: 2px;
        background: transparent;
        color: #454b41;
        font: inherit;
        font-size: 13px;
        line-height: 1.5;
        text-decoration: underline;
        text-underline-offset: 3px;
        cursor: pointer;
      }
      .fe-sample-popup__continue:hover { color: #344c33; }
      .fe-sample-popup__close:focus-visible,
      .fe-sample-popup__basket:focus-visible,
      .fe-sample-popup__continue:focus-visible {
        outline: 2px solid #456444;
        outline-offset: 3px;
        box-shadow: none;
      }
      @media (max-width: 749px) {
        #fe-sample-popup {
          top: auto;
          right: max(12px, env(safe-area-inset-right));
          bottom: max(16px, env(safe-area-inset-bottom));
          left: max(12px, env(safe-area-inset-left));
          width: auto;
          max-height: calc(100dvh - 32px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
        }
        .fe-sample-popup__header { padding: 12px 12px 12px 16px; }
        .fe-sample-popup__heading { font-size: 15px; }
        .fe-sample-popup__body { padding: 16px; }
        .fe-sample-popup__product {
          grid-template-columns: 64px minmax(0, 1fr);
          gap: 12px;
          min-height: 64px;
        }
        .fe-sample-popup__image { width: 64px; height: 64px; }
        .fe-sample-popup__title { font-size: 14px; }
      }
      @media (prefers-reduced-motion: reduce) {
        #fe-sample-popup { transition: none; }
      }
    `;
    document.head.appendChild(style);

    popup = document.createElement("div");
    popup.id = "fe-sample-popup";
    popup.setAttribute("role", "dialog");
    popup.setAttribute("aria-modal", "false");
    popup.setAttribute("aria-labelledby", "fe-sample-popup-heading");
    popup.setAttribute("aria-describedby", "fe-sample-popup-message");
    popup.hidden = true;
    popup.innerHTML = `
      <div class="fe-sample-popup__header">
        <h2 class="fe-sample-popup__heading" id="fe-sample-popup-heading"></h2>
        <button class="fe-sample-popup__close" type="button" aria-label="Close sample confirmation"><svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false"><path d="M4 4L14 14M14 4L4 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>
      </div>
      <div class="fe-sample-popup__body">
        <div class="fe-sample-popup__product">
          <img class="fe-sample-popup__image" alt="" width="76" height="76" hidden>
          <div>
            <p class="fe-sample-popup__title"></p>
            <p class="fe-sample-popup__message" id="fe-sample-popup-message"></p>
          </div>
        </div>
        <a class="fe-sample-popup__basket" href="${this.route("cart")}">View basket</a>
        <button class="fe-sample-popup__continue" type="button">Continue shopping</button>
      </div>
    `;

    popup
      .querySelector(".fe-sample-popup__close")
      .addEventListener("click", () => this.hidePopup());
    popup
      .querySelector(".fe-sample-popup__continue")
      .addEventListener("click", () => this.hidePopup());
    document.body.appendChild(popup);

    return popup;
  },

  showPopup({ heading, title, message, image, isError = false }) {
    const popup = this.ensurePopup();
    const header = popup.querySelector(".fe-sample-popup__header");
    const imageElement = popup.querySelector(".fe-sample-popup__image");

    header.classList.toggle("is-error", isError);
    popup.querySelector(".fe-sample-popup__heading").textContent = heading;
    popup.querySelector(".fe-sample-popup__title").textContent = title;
    popup.querySelector(".fe-sample-popup__message").textContent = message;

    if (image) {
      imageElement.src = image;
      imageElement.alt = title;
      imageElement.hidden = false;
    } else {
      imageElement.removeAttribute("src");
      imageElement.alt = "";
      imageElement.hidden = true;
    }

    popup.hidden = false;
    // Force the initial hidden state to paint before the entrance transition.
    void popup.offsetWidth;
    popup.classList.add("fe-sample-popup--visible");

    window.clearTimeout(this.popupTimer);
    this.popupTimer = window.setTimeout(
      () => this.hidePopup(),
      this.popupDuration
    );
  },

  hidePopup() {
    const popup = document.getElementById("fe-sample-popup");
    if (!popup) return;
    popup.classList.remove("fe-sample-popup--visible");
    window.clearTimeout(this.popupTimer);
    window.setTimeout(() => {
      if (!popup.classList.contains("fe-sample-popup--visible")) {
        popup.hidden = true;
      }
    }, 220);
  },

  showSuccessPopup(item, count) {
    const remaining = Math.max(this.maxSamples - count, 0);
    const image = item?.featured_image?.url || item?.image || "";
    const productTitle = item?.product_title || "Your free sample";
    const title = /sample/i.test(productTitle)
      ? productTitle
      : `${productTitle} – Sample`;

    this.showPopup({
      heading: `${count}/${this.maxSamples} free samples added to your basket`,
      title,
      message:
        remaining === 0
          ? "Your four samples are ready — compare them at home."
          : `${remaining} more free ${remaining === 1 ? "sample" : "samples"} available.`,
      image,
    });
  },

  showLimitMessage(count = this.maxSamples) {
    this.showPopup({
      heading: `${Math.min(count, this.maxSamples)}/${this.maxSamples} samples selected`,
      title: "Your free sample selection is complete",
      message: "You can order up to four free samples. View your basket to review or change them.",
    });
  },

  showErrorPopup(message) {
    this.showPopup({
      heading: "We couldn’t add that sample",
      title: "Please try again",
      message,
      isError: true,
    });
  },
};

window.FloorEasySampleCart = FloorEasySampleCart;

if (!customElements.get("sample-button")) {
  customElements.define(
    "sample-button",
    class SampleButton extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector(".quickadd-sample");
        if (!this.button) return;

        this.variantId = this.button.dataset.variantid;
        if (!this.variantId) return;

        this.prepareLabel();

        if (!this.boundClickHandler) {
          this.boundClickHandler = this.onClickHandler.bind(this);
          this.button.addEventListener("click", this.boundClickHandler);
        }

        FloorEasySampleCart.register(this);
      }

      disconnectedCallback() {
        FloorEasySampleCart.unregister(this);
      }

      prepareLabel() {
        this.label = this.button.querySelector(".sample-button__label");
        if (!this.label) {
          this.label = Array.from(this.button.children).find(
            (element) =>
              element.tagName === "SPAN" &&
              !element.classList.contains("loading__spinner")
          );
        }

        if (!this.label) {
          const textNodes = Array.from(this.button.childNodes).filter(
            (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
          );
          this.label = document.createElement("span");
          this.label.className = "sample-button__label";
          this.label.textContent = textNodes
            .map((node) => node.textContent.trim())
            .join(" ");

          const spinner = this.button.querySelector(".loading__spinner");
          this.button.insertBefore(this.label, spinner || null);
          textNodes.forEach((node) => node.remove());
        } else {
          this.label.classList.add("sample-button__label");
        }

        this.originalLabel = this.label.textContent.trim() || "Add Free Sample";
      }

      onClickHandler(event) {
        event.preventDefault();
        event.stopPropagation();
        FloorEasySampleCart.add(this);
      }

      renderState(state) {
        const isAdded = state.variantIds.has(String(this.variantId));
        const isActive = state.activeInstance === this;

        this.button.classList.toggle("sample-added", isAdded);
        this.button.classList.toggle(
          "sample-limit-reached",
          !isAdded && state.limitReached
        );
        this.button.classList.toggle("loading", state.busy && isActive);

        const spinner = this.button.querySelector(".loading__spinner");
        if (spinner) spinner.classList.toggle("hidden", !(state.busy && isActive));

        if (isAdded) {
          this.label.textContent = "Sample Added ✓";
        } else if (state.limitReached) {
          this.label.textContent = "4 Sample Limit Reached";
        } else if (state.busy && isActive) {
          this.label.textContent = "Adding Sample…";
        } else {
          this.label.textContent = this.originalLabel;
        }

        const shouldDisable = this.button.dataset.sampleAvailable === "false" || isAdded || state.limitReached || state.busy;
        this.button.disabled = shouldDisable;
        this.button.setAttribute("aria-disabled", String(shouldDisable));
      }
    }
  );
}

if (!FloorEasySampleCart.pageshowBound) {
  FloorEasySampleCart.pageshowBound = true;
  window.addEventListener("pageshow", () => {
    FloorEasySampleCart.loadCart(true).catch((error) => console.error(error));
  });
}


